/**
 * FORGE Portal — Security Module
 * Input Sanitization, CSP, Rate Limiting, Prompt Injection Protection
 */

/** HTML-Sonderzeichen escapen — verhindert XSS */
export function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/** Prompt Injection Patterns */
const INJECTION_PATTERNS = [
  /ignore\s+(?:all\s+)?(?:previous|above|prior)\s+instructions?/gi,
  /forget\s+(?:everything|all|your\s+instructions)/gi,
  /you\s+are\s+now\s+(?:a|an|the|in)\s+/gi,
  /act\s+as\s+(?:if\s+you\s+(?:are|were)|a|an)\s+/gi,
  /disregard\s+(?:all|your|the)\s+/gi,
  /override\s+(?:your|all|the)\s+/gi,
  /system\s*:\s*you\s+are/gi,
  /\[INST\]|\[\/INST\]|<\|im_start\|>|<\|im_end\|>/g,
  /<!--.*?-->/gs,
  /<script[^>]*>.*?<\/script>/gis,
  /javascript:/gi,
  /on\w+\s*=\s*["']?[^"'\s>]+/gi,
];

/** User-Input sanitieren — Prompt Injection + XSS-Schutz */
/** Die eigentliche Arbeit: Muster der Prompt-Injection unschädlich machen. */
function _entschaerfen(clean) {
  INJECTION_PATTERNS.forEach(p => {
    if (p.test(clean)) {
      console.warn('[SECURITY] Mögliche Injection erkannt');
      clean = clean.replace(p, '[ENTFERNT]');
    }
  });
  return clean;
}

/** Kurze Eingaben: Name, Ziel, Zielgruppe. 2000 Zeichen sind dort reichlich. */
export function sanitizeInput(str) {
  if (typeof str !== 'string') return '';
  return _entschaerfen(str.trim().substring(0, 2000));
}

/**
 * Obergrenze für einen Leitfaden (Coding Guide, Design Guide).
 *
 * Warum überhaupt eine Grenze: der Text geht WÖRTLICH in die Prompts von TARA
 * und DEV — bei jeder Story, und bei einer Nacharbeit erneut. 8000 Zeichen sind
 * rund 2000 Token je Aufruf; ein Markenhandbuch mit 40 Seiten wäre ein
 * Vielfaches davon, auf jeden Loop gerechnet, ohne dass die zusätzlichen Seiten
 * (Druckmaße, Logo-Schutzraum, Anzeigentexte) für den Code etwas hergeben.
 */
export const GUIDE_MAX = 8000;

/**
 * Lange Leitfäden: dieselbe Entschärfung, aber nicht auf 2000 Zeichen gestutzt.
 *
 * ⚠️ ANLASS (07.09.2026): `sanitizeInput` schneidet bei 2000 Zeichen ab — still.
 * Für einen Namen ist das reichlich, für einen Leitfaden ist es ein Datenverlust
 * mitten im Satz: FORGEs eigener Design-Vorschlag war 2450 Zeichen lang, die
 * letzten 450 (Bewegung, Tonalität) fielen beim Speichern weg, ohne dass jemand
 * es erfuhr. Dasselbe galt seit jeher für den Coding Guide.
 *
 * Der Aufrufer vergleicht die Länge vorher und nachher und SAGT es, wenn gekürzt
 * wurde — eine stille Kürzung ist genau die Sorte Fehler, die man erst Wochen
 * später an einem unerklärlichen Ergebnis bemerkt.
 */
export function sanitizeGuide(str, max = GUIDE_MAX) {
  if (typeof str !== 'string') return '';
  return _entschaerfen(str.trim().substring(0, max));
}

/** Rate Limiting — max N Calls pro Minute */
const _calls = {};
export function rateLimit(key, maxPerMinute) {
  const now = Date.now();
  if (!_calls[key]) _calls[key] = [];
  _calls[key] = _calls[key].filter(t => now - t < 60000);
  if (_calls[key].length >= maxPerMinute) return false;
  _calls[key].push(now);
  return true;
}

/** Content Security Policy per Meta-Tag */
export function injectCSP() {
  if (document.querySelector('meta[http-equiv="Content-Security-Policy"]')) return;
  const meta = document.createElement('meta');
  meta.setAttribute('http-equiv', 'Content-Security-Policy');
  meta.setAttribute('content', [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline'",
    "style-src 'self' 'unsafe-inline'",
    "font-src 'self' data:",
    "img-src 'self' data: https://api.qrserver.com",
    "connect-src 'self' https://*.supabase.co https://schulz-solutions.app.n8n.cloud https://api.zippopotam.us",
    // frame-ancestors wird von Browsern im <meta> IGNORIERT — als echter HTTP-Header in nginx.conf gesetzt.
  ].join('; '));
  document.head.insertBefore(meta, document.head.firstChild);
}

/** Referrer-Policy (Clickjacking-Schutz via X-Frame-Options/frame-ancestors läuft als
 *  echter HTTP-Header über nginx.conf — im <meta> würde der Browser ihn ignorieren). */
export function injectSecurityHeaders() {
  const rp = document.createElement('meta');
  rp.setAttribute('name', 'referrer');
  rp.setAttribute('content', 'strict-origin-when-cross-origin');
  document.head.appendChild(rp);
}

/** TOTP RFC 6238 — prüft ob Code zum Secret passt */
export async function totpCheck(secret, userCode) {
  const b32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = '';
  for (const ch of secret.toUpperCase()) {
    const v = b32.indexOf(ch);
    if (v >= 0) bits += v.toString(2).padStart(5, '0');
  }
  const bytes = [];
  for (let i = 0; i + 8 <= bits.length; i += 8)
    bytes.push(parseInt(bits.slice(i, i + 8), 2));

  const key = await crypto.subtle.importKey(
    'raw', new Uint8Array(bytes),
    { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']
  );
  const now = Math.floor(Date.now() / 1000);
  for (let d = -1; d <= 1; d++) {
    const T = Math.floor((now + d * 30) / 30);
    const tb = new ArrayBuffer(8);
    new DataView(tb).setUint32(4, T >>> 0, false);
    const sig = await crypto.subtle.sign('HMAC', key, tb);
    const h = new Uint8Array(sig);
    const o = h[19] & 0xf;
    const code = ((h[o] & 0x7f) << 24 | h[o+1] << 16 | h[o+2] << 8 | h[o+3]) % 1000000;
    if (code.toString().padStart(6, '0') === userCode) return true;
  }
  return false;
}

/** SHA-256 Hash */
export async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}
